import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from './Modal'
import { SettingBaseWorkTimeMember } from '../../../states/types/setting'
import { secToHour, secToMinute, secToTwoDigitsHourMinute } from '../../../utils/date'
import { pickNumber } from '../../../utils/string'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface Error {
  type: string
  message: string
  target?: string[]
}

interface WorkTime {
  use_common_setting: boolean
  base_work_start_time?: string | null
  base_work_end_time?: string | null
  base_break_time?: number
}

interface BreakTime {
  use_common_setting: boolean
  break_times?: {
    start_time: string
    end_time: string
  }[]
}

interface SubmitParam {
  memberIds: number[]
  workTime: WorkTime
  breakTime: BreakTime
}

interface Props {
  targetMemberList: SettingBaseWorkTimeMember[]
  isDisplayModal: boolean
  submitCallback: (data: SubmitParam) => void
  closeCallback: VoidFunction
}

const DISPLAY_MODAL_MEMBER_LIMIT = 30

const SELECT_INTERVAL_WORK_TIME_MINUTES = 5

const SELECT_INTERVAL_BREAK_TIME_MINUTES = 15

const BREAK_TIME_LIMIT = 5

const SettingBaseWorkTimeModal: FC<Props> = (props) => {
  const {
    targetMemberList,
    isDisplayModal,
    submitCallback,
    closeCallback,
  } = props

  const [isBaseWorkTime, setIsBaseWorkTime] = useState<boolean>(false)
  const [baseWorkStartTime, setBaseWorkStartTime] = useState<string>("")
  const [baseWorkEndTime, setBaseWorkEndTime] = useState<string>("")
  const [baseBreakTime, setBaseBreakTime] = useState<number>(0)
  const [isScheduledBreakTimes, setIsScheduledBreakTimes] = useState<boolean>(false)
  const [scheduledBreakTimes, setScheduledBreakTimes] = useState<{start_time: string, end_time: string}[]>([])

  const [isUseCommonWorkTime, setIsUseCommonWorkTime] = useState<boolean>(false)
  const [isUseCommonScheduledBreakTimes, setIsUseCommonScheduledBreakTimes] = useState<boolean>(false)

  const [errors, setErrors] = useState<Error[]>([])

  const workTimeList = useMemo(() => {
    if (SELECT_INTERVAL_WORK_TIME_MINUTES > 60) return []
    const tmp = Array(1440 / SELECT_INTERVAL_WORK_TIME_MINUTES).fill(null).map((_v, i) => (
      secToTwoDigitsHourMinute(60 * (i * SELECT_INTERVAL_WORK_TIME_MINUTES) + (3600 * 5))
    ))
    return tmp
  }, [])

  const breakTimeList = useMemo(() => {
    if (SELECT_INTERVAL_BREAK_TIME_MINUTES > 60) return []
    const tmp = Array((960 + SELECT_INTERVAL_BREAK_TIME_MINUTES) / SELECT_INTERVAL_BREAK_TIME_MINUTES).fill(null).map((_v, i) => {
      const hours = secToHour(60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES))
      const minutes = secToMinute(60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES))
      return ({
        key: `${hours ? `${hours}時間` : ""}${minutes ? `${minutes}分` : ""}`,
        value: 60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES),
      })
    })
    return tmp
  }, [])

  const workTimeSeconds = useMemo(() => {
    if (!baseWorkStartTime || !baseWorkEndTime) return 0
    const [tmpStartHours, tmpStartMinutes] = baseWorkStartTime.split(":")
    const [tmpEndHours, tmpEndMinutes] = baseWorkEndTime.split(":")
    return ((Number(tmpEndHours) - Number(tmpStartHours)) * 3600) + ((Number(tmpEndMinutes) - Number(tmpStartMinutes)) * 60)
  }, [baseWorkStartTime, baseWorkEndTime])

  const scheduledBreakTimeSeconds = useMemo(() => {
    if (!scheduledBreakTimes.length) return 0
    return scheduledBreakTimes.reduce((s, v) => {
      const [tmpStartHours, tmpStartMinutes] = v.start_time.split(":")
      const [tmpEndHours, tmpEndMinutes] = v.end_time.split(":")
      return s + ((Number(tmpEndHours) - Number(tmpStartHours)) * 3600) + ((Number(tmpEndMinutes) - Number(tmpStartMinutes)) * 60)
    }, 0)
  }, [scheduledBreakTimes])

  const changeValue = (changeCallback: (value: string | number) => void, value: string | number) => {
    changeCallback(value)
  }

  const buildWorkTimeSelectBox = (currentValue: string, changeCallback: (value: number | string) => void) => {
    if (!currentValue) {
      return (<>指定なし</>)
    }
    return (
      <select
        style={{
          border: "solid 1px #CCCCCC",
          borderRadius: "4px",
          padding: "4px",
          backgroundColor: "#FFFFFF",
          appearance: "auto",
        }}
        value={currentValue}
        onChange={(e) => changeValue(changeCallback, e.target.value)}
      >
        {
          workTimeList.map((v) => (
            <option value={v} key={`${v}`}>
              {v || "指定なし"}
            </option>
          ))
        }
      </select>
    )
  }

  const buildBreakTimeSelectBox = (currentValue: number, changeCallback: (value: number | string) => void) => (
    <select
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "4px",
        padding: "4px",
        backgroundColor: "#FFFFFF",
        appearance: "auto",
      }}
      value={currentValue}
      onChange={(e) => changeValue(changeCallback, Number(e.target.value))}
    >
      {
        breakTimeList.map((v) => (
          <option value={v.value} key={`break_${v.value}`}>
            {v.key || "指定なし"}
          </option>
        ))
      }
    </select>
  )

  const buildCheckBox = (value: boolean) => (
    value ? (
      <FontAwesomeIcon
        icon={["fas", "check-square"]}
        style={{ color: "#008800" }}
        fixedWidth
      />
    ) : (
      <FontAwesomeIcon
        icon={["far", "square"]}
        style={{ color: "#999999" }}
        fixedWidth
      />
    )
  )

  const buildSecToTime = (sec: number) => {
    let tmpText = ""
    if (Number(secToHour(sec))) {
      tmpText += `${secToHour(sec)}時間`
    }
    if (Number(secToMinute(sec))) {
      tmpText += `${secToMinute(sec)}分`
    } else {
      tmpText += "0分"
    }
    return tmpText
  }

  const timeAdjuster = (time: string | null, to29 = false) => {
    if (to29) {
      if (time) {
        const [hours, minutes] = time.split(":")
        if (Number(hours) >= 5) {
          return `0${time}`.slice(-5)
        }
        return `0${Number(hours) + 24}:${minutes}`.slice(-5)
      }
    }
    if (time) {
      const [hours, minutes] = time.split(":")
      if (Number(hours) < 24) {
        return `0${time}`.slice(-5)
      }
      return `0${Number(hours) - 24}:${minutes}`.slice(-5)
    }
    return null
  }

  const changeSettingBaseWorkTime = () => {
    if (isBaseWorkTime) {
      setBaseWorkStartTime("")
      setBaseWorkEndTime("")
      setBaseBreakTime(0)
    } else {
      setBaseWorkStartTime(timeAdjuster(targetMemberList[0].work_time.base_work_start_time || null, true) || "09:00")
      setBaseWorkEndTime(timeAdjuster(targetMemberList[0].work_time.base_work_end_time || null, true) || "18:00")
      setBaseBreakTime(targetMemberList[0].work_time.base_break_time || 3600)
    }
    setIsBaseWorkTime((v) => !v)
  }

  const changeSettingScheduledBreakTime = () => {
    if (isScheduledBreakTimes) {
      setScheduledBreakTimes([])
    } else {
      setScheduledBreakTimes(
        targetMemberList[0].break_time.break_times.length
          ? targetMemberList[0].break_time.break_times.map((v) => ({
            start_time: timeAdjuster(v.start_time, true) || "12:00",
            end_time: timeAdjuster(v.end_time, true) || "13:00",
          }))
          : [{ start_time: "12:00", end_time: "13:00" }],
      )
    }
    setIsScheduledBreakTimes((v) => !v)
  }

  const addBreakTime = () => {
    let tmpTime = { start_time: "12:00", end_time: "13:00" }
    if (scheduledBreakTimes.length) {
      const targetBreakTime = scheduledBreakTimes[scheduledBreakTimes.length - 1]
      const [newEndHours, newEndMinutes] = targetBreakTime.end_time.split(':')
      tmpTime = {
        start_time: targetBreakTime.end_time,
        end_time: `${Number(newEndHours) < 28 ? `00${Number(newEndHours) + 1}`.slice(-2) : "28"}:${newEndMinutes}`,
      }
    }
    setScheduledBreakTimes((cur) => [...cur, tmpTime])
  }

  const delBreakTime = (index: number) => {
    if (scheduledBreakTimes.length === 1) {
      setScheduledBreakTimes([])
      setIsScheduledBreakTimes(false)
    } else {
      setScheduledBreakTimes((cur) => cur.filter((_v, i) => i !== index))
    }
  }

  const changeUseCommon = (callback: (v: React.SetStateAction<boolean>) => void) => {
    callback((v) => !v)
  }

  const close = () => {
    closeCallback()
    setErrors([])
  }

  const submit = () => {
    const tmpWorkTime: WorkTime = { use_common_setting: isUseCommonWorkTime }
    if (!isUseCommonWorkTime) {
      tmpWorkTime.base_work_start_time = timeAdjuster(baseWorkStartTime) || null
      tmpWorkTime.base_work_end_time = timeAdjuster(baseWorkEndTime) || null
      tmpWorkTime.base_break_time = baseBreakTime
    }
    const tmpBreakTime: BreakTime = { use_common_setting: isUseCommonScheduledBreakTimes }
    if (!isUseCommonScheduledBreakTimes) {
      tmpBreakTime.break_times = scheduledBreakTimes.map((v) => ({
        start_time: timeAdjuster(v.start_time) || "12:00",
        end_time: timeAdjuster(v.end_time) || "13:00",
      }))
    }
    const param: SubmitParam = {
      memberIds: targetMemberList.map((v) => v.id),
      workTime: tmpWorkTime,
      breakTime: tmpBreakTime,
    }
    submitCallback(param)
    close()
  }

  useEffect(() => {
    const target = targetMemberList[0]
    if (!target) return
    setIsUseCommonWorkTime(target.work_time.use_common_setting)
    if (!target.work_time.use_common_setting) {
      setBaseWorkStartTime(timeAdjuster(target.work_time.base_work_start_time, true) || "")
      setBaseWorkEndTime(timeAdjuster(target.work_time.base_work_end_time, true) || "")
      setIsBaseWorkTime(!!target.work_time.base_work_start_time || !!target.work_time.base_work_end_time)
      setBaseBreakTime(target.work_time.base_break_time)
    }
    setIsUseCommonScheduledBreakTimes(target.break_time.use_common_setting)
    if (!target.break_time.use_common_setting) {
      setScheduledBreakTimes(target.break_time.break_times)
      setIsScheduledBreakTimes(!!target.break_time.break_times.length)
    }
  }, [targetMemberList])

  useEffect(() => {
    const tmpErrors: Error[] = []
    if (baseWorkStartTime && baseWorkEndTime && Number(pickNumber(baseWorkStartTime)) >= Number(pickNumber(baseWorkEndTime))) {
      tmpErrors.push({
        type: "work_time",
        message: "標準勤務時間の設定が誤っています。勤務終了時間は勤務開始時間より前の時間を指定してください。",
      })
    }
    if (isBaseWorkTime && (workTimeSeconds - baseBreakTime <= 0)) {
      tmpErrors.push({
        type: "work_time",
        message: "標準勤務時間の設定が誤っています。休憩予定時間は勤務時間以下にしてください。",
      })
    }
    scheduledBreakTimes.forEach((v, i) => {
      const startNum = Number(pickNumber(v.start_time))
      const endNum = Number(pickNumber(v.end_time))
      if (isScheduledBreakTimes && (endNum - startNum <= 0)) {
        tmpErrors.push({
          type: "break_time",
          message: "標準休憩時間の設定が誤っています。休憩予定終了時間は休憩予定開始時間より前の時間を指定してください。",
        })
      }
      scheduledBreakTimes.filter((_, j) => j > i).forEach((w) => {
        const tmpStartNum = Number(pickNumber(w.start_time))
        const tmpEndNum = Number(pickNumber(w.end_time))
        if (!(startNum >= tmpEndNum || endNum <= tmpStartNum)) {
          tmpErrors.push({
            type: "break_time",
            message: "標準休憩時間の設定が誤っています。設定時間が重複しています。",
          })
        }
      })
    })
    if (tmpErrors.length) {
      setErrors(tmpErrors)
    } else {
      setErrors([])
    }
  }, [baseWorkStartTime, baseWorkEndTime, baseBreakTime, scheduledBreakTimes, workTimeSeconds])

  return (
    <Modal
      show={isDisplayModal}
      wrapperClass="w900"
      closeCallback={close}
      closeable
    >
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "20px",
            marginBottom: "16px",
          }}
        >
          標準勤務時刻・休憩時刻設定
        </div>
        <div
          style={{
            display: "flex",
            flexFlow: "column",
            gap: "16px",
          }}
        >
          <div
            style={{
              border: "solid 1px #999999",
              borderRadius: "4px",
              padding: "8px",
              display: "flex",
              flexFlow: "column",
              gap: "8px",
              backgroundColor: "#F3F3f3",
            }}
          >
            <div>
              対象
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
              }}
            >
              {
                targetMemberList.slice(0, DISPLAY_MODAL_MEMBER_LIMIT).map((v) => (
                  <div
                    key={`member_${v.id}`}
                    style={{
                      border: "solid 1px #999999",
                      borderRadius: "20px",
                      padding: "2px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "10px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <img
                      alt={v.name}
                      src={v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                      style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                    />
                    {v.name}
                  </div>
                ))
              }
              {
                targetMemberList.length > DISPLAY_MODAL_MEMBER_LIMIT && (
                  <div
                    style={{
                      padding: "3px",
                    }}
                  >
                    {`…他${targetMemberList.length - DISPLAY_MODAL_MEMBER_LIMIT}名`}
                  </div>
                )
              }
            </div>
          </div>
          <div
            style={{
              border: "solid 1px #999999",
              borderRadius: "4px",
              padding: "8px",
              display: "flex",
              flexFlow: "column",
              gap: "8px",
            }}
          >
            <div>
              標準勤務時刻・休憩時刻設定
            </div>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  position: "relative",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  backgroundColor: "#f3feff",
                  padding: "16px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                {
                  isUseCommonWorkTime && (
                    <div
                      style={{
                        position: "absolute",
                        top: "28px",
                        left: 0,
                        width: "100%",
                        height: "calc(100% - 28px)",
                        backgroundColor: "rgba(243, 243, 243, 0.8)",
                        zIndex: "2000",
                      }}
                    />
                  )
                }
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#f3f3f3",
                    padding: "8px",
                    margin: "-16px -16px 0 -16px",
                    borderRadius: "4px 4px 0 0",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    勤務時間設定
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                      fontSize: "10px",
                      color: "#666666",
                    }}
                    onClick={() => changeUseCommon(setIsUseCommonWorkTime)}
                    role="none"
                  >
                    {buildCheckBox(isUseCommonWorkTime)}
                    全体設定に準拠する
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        userSelect: "none",
                        cursor: "pointer",
                      }}
                      onClick={changeSettingBaseWorkTime}
                      role="none"
                    >
                      {buildCheckBox(isBaseWorkTime)}
                      標準勤務時間を設定する
                    </div>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      backgroundColor: "#FFFFFF",
                      fontSize: "11px",
                      minHeight: "35px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      勤務開始
                      {buildWorkTimeSelectBox(baseWorkStartTime, (v) => setBaseWorkStartTime(v.toString()))}
                      ~
                      勤務終了
                      {buildWorkTimeSelectBox(baseWorkEndTime, (v) => setBaseWorkEndTime(v.toString()))}
                      うち休憩予定時間
                      {buildBreakTimeSelectBox(baseBreakTime, (v) => setBaseBreakTime(Number(v)))}
                    </div>
                    {
                      !isBaseWorkTime ? (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(243, 243, 243, 0.8)",
                            borderRadius: "4px",
                            zIndex: "2000",
                          }}
                        />
                      ) : (
                        <>
                          {
                            !errors.filter((v) => v.type === "work_time").length && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  color: "#999999",
                                }}
                              >
                                標準勤務時間
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  {buildSecToTime(baseBreakTime ? workTimeSeconds - baseBreakTime : workTimeSeconds)}
                                  {!!baseBreakTime && `(休憩予定時間:${buildSecToTime(baseBreakTime)})`}
                                </div>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                  </div>
                </div>
                {
                  errors.filter((v) => v.type === "work_time").map((v) => (
                    <div
                      style={{
                        border: "solid 1px #FF0000",
                        borderRadius: "4px",
                        color: "#FF0000",
                        backgroundColor: "#fff3f3",
                        padding: "8px",
                      }}
                    >
                      {v.message}
                    </div>
                  ))
                }
              </div>
              <div
                style={{
                  position: "relative",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  backgroundColor: "#f3fff4",
                  padding: "16px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                {
                  isUseCommonScheduledBreakTimes && (
                    <div
                      style={{
                        position: "absolute",
                        top: "28px",
                        left: 0,
                        width: "100%",
                        height: "calc(100% - 28px)",
                        backgroundColor: "rgba(243, 243, 243, 0.8)",
                        zIndex: "2000",
                      }}
                    />
                  )
                }
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#f3f3f3",
                    padding: "8px",
                    margin: "-16px -16px 0 -16px",
                    borderRadius: "4px 4px 0 0",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    休憩時間をあらかじめ登録する
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                      fontSize: "10px",
                      color: "#666666",
                    }}
                    onClick={() => changeUseCommon(setIsUseCommonScheduledBreakTimes)}
                    role="none"
                  >
                    {buildCheckBox(isUseCommonScheduledBreakTimes)}
                    全体設定に準拠する
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        userSelect: "none",
                        cursor: "pointer",
                      }}
                      onClick={changeSettingScheduledBreakTime}
                      role="none"
                    >
                      {buildCheckBox(isScheduledBreakTimes)}
                      休憩時間をあらかじめ登録する
                    </div>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      backgroundColor: "#FFFFFF",
                      fontSize: "11px",
                      minHeight: "35px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                        }}
                      >
                        {
                          scheduledBreakTimes.map((v, i) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              休憩開始
                              { /* eslint-disable-next-line max-len */}
                              {buildWorkTimeSelectBox(v.start_time, (value) => setScheduledBreakTimes((cur) => cur.map((w, j) => (i === j ? { ...w, start_time: value.toString() } : w))))}
                              ~
                              休憩終了
                              { /* eslint-disable-next-line max-len */}
                              {buildWorkTimeSelectBox(v.end_time, (value) => setScheduledBreakTimes((cur) => cur.map((w, j) => (i === j ? { ...w, end_time: value.toString() } : w))))}
                              {
                                !!v.start_time && (
                                  <button
                                    type="button"
                                    onClick={() => delBreakTime(i)}
                                    style={{
                                      color: "#FF0000",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'trash']} />
                                  </button>
                                )
                              }
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    {
                      !isScheduledBreakTimes ? (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(243, 243, 243, 0.8)",
                            borderRadius: "4px",
                            zIndex: "2000",
                          }}
                        />
                      ) : (
                        <>
                          {
                            !errors.filter((v) => v.type === "break_time").length && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  color: "#999999",
                                }}
                              >
                                合計
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  {buildSecToTime(scheduledBreakTimeSeconds)}
                                </div>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                  </div>
                  {
                    isScheduledBreakTimes && scheduledBreakTimes.length < BREAK_TIME_LIMIT && (
                      <button
                        type="button"
                        onClick={addBreakTime}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "4px",
                          border: "solid 1px #008800",
                          borderRadius: "4px",
                          color: "#008800",
                          backgroundColor: "#e1ffe0",
                        }}
                      >
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                        休憩時間を追加
                      </button>
                    )
                  }
                </div>
                {
                  errors.filter((v) => v.type === "break_time").map((v) => (
                    <div
                      style={{
                        border: "solid 1px #FF0000",
                        borderRadius: "4px",
                        color: "#FF0000",
                        backgroundColor: "#fff3f3",
                        padding: "8px",
                      }}
                    >
                      {v.message}
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              marginTop: "32px",
            }}
          >
            <button
              type="button"
              style={{
                border: `solid 1px #999999`,
                borderRadius: "4px",
                backgroundColor: "#EEEEEE",
                color: "#666666",
                minWidth: "130px",
                textAlign: "center",
              }}
              onClick={close}
            >
              キャンセル
            </button>
            <button
              type="button"
              style={{
                border: `solid 1px ${errors.length ? "#a7a7a7" : "#507DC0"}`,
                borderRadius: "4px",
                backgroundColor: errors.length ? "#a7a7a7" : "#507DC0",
                color: "#FFFFFF",
                minWidth: "130px",
                textAlign: "center",
                cursor: errors.length ? "not-allowed" : "pointer",
              }}
              onClick={submit}
              disabled={!!errors.length}
            >
              設定する
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}

export default SettingBaseWorkTimeModal
